import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Award, Target, Users, Shield, Building, CheckCircle, Home, History } from 'lucide-react';

export const About = () => {
  const values = [
    {
      icon: Award,
      title: 'Excellence',
      description: 'Un engagement constant vers la qualité et le dépassement des attentes.',
    },
    {
      icon: Shield,
      title: 'Fiabilité',
      description: 'Des garanties solides et un respect strict des engagements pris.',
    },
    {
      icon: Target,
      title: 'Innovation',
      description: 'Des solutions modernes pour construire l\'habitat de demain.',
    },
    {
      icon: Users,
      title: 'Partenariat',
      description: 'Une collaboration étroite avec tous les acteurs du projet.',
    },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#F25F19] font-bold uppercase tracking-widest mb-4">À propos</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0A0A0A] uppercase tracking-tighter leading-tight mb-6">
              Une vision,<br />
              <span className="text-[#F25F19]">une expertise</span>
            </h1>
            <p className="text-lg md:text-xl text-[#71717A] max-w-xl">
              PRO HOME Développement s'engage depuis plusieurs années à construire 
              l'avenir de l'habitat social avec qualité et innovation.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section data-testid="mission-section" className="py-20 lg:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#F25F19] font-bold uppercase tracking-widest mb-4">Notre mission</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tighter mb-8">
                Bâtir l'avenir de l'habitat
              </h2>
              <p className="text-white/70 text-lg mb-6">
                PRO HOME Développement est une entreprise dédiée à la qualité et au service 
                dans le domaine du BTP. Notre mission est de créer des logements de qualité 
                accessibles à tous, en partenariat avec les bailleurs sociaux.
              </p>
              <p className="text-white/70 text-lg mb-8">
                Nous opérons dans un secteur en forte demande, l'habitat social, avec un 
                montage solide et sécurisé par les garanties constructeurs. Notre expertise 
                nous permet d'accompagner nos partenaires à chaque étape de leurs projets.
              </p>
              <div className="flex gap-8">
                <div>
                  <p className="text-4xl font-bold text-[#F25F19] mono">+250</p>
                  <p className="text-white/60 text-sm">Logements livrés</p>
                </div>
              </div>
              <p className="text-white/40 text-sm mt-6">SIRET : 93791839900018</p>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/3760529/pexels-photo-3760529.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Notre équipe en réunion"
                className="rounded-sm w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* President Section */}
      <section data-testid="president-section" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="bg-white p-8 rounded-sm">
                <p className="text-[#F25F19] font-bold uppercase tracking-widest mb-4">Notre Président</p>
                <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter mb-2">
                  Guillaume TARI
                </h2>
                <p className="text-[#71717A] font-medium mb-6">Président de PRO HOME Développement</p>
                
                <p className="text-[#71717A] mb-4">
                  Entrepreneur français issu d'une famille avec une tradition dans les secteurs 
                  viticole et immobilier, Guillaume TARI apporte une vision stratégique unique 
                  à PRO HOME Développement.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#F25F19] mt-0.5 flex-shrink-0" />
                    <p className="text-[#71717A]">
                      Direction du Domaine de la Bégude à Bandol, vignoble renommé
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#F25F19] mt-0.5 flex-shrink-0" />
                    <p className="text-[#71717A]">
                      Expérience solide en développement immobilier et valorisation de patrimoine
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#F25F19] mt-0.5 flex-shrink-0" />
                    <p className="text-[#71717A]">
                      Actionnaire de Barnes Luxembourg (Europe Centrale et Moyen-Orient)
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#F25F19] mt-0.5 flex-shrink-0" />
                    <p className="text-[#71717A]">
                      Co-fondateur de New Energy Partners dans les énergies renouvelables
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_business-web-creator-3/artifacts/35emfbtr_IMG_2147.jpeg"
                  alt="Guillaume TARI - Président de PRO HOME Développement"
                  className="rounded-sm w-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0A0A0A] to-transparent p-8">
                  <p className="text-white font-bold text-xl">Guillaume TARI</p>
                  <p className="text-[#F25F19]">Président</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section data-testid="values-section" className="py-20 lg:py-32 bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#F25F19] font-bold uppercase tracking-widest mb-4">Nos valeurs</p>
            <h2 className="section-title">Ce qui nous guide</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="text-center p-8 bg-[#F4F4F5] rounded-sm hover:bg-[#0A0A0A] group transition-colors duration-300"
              >
                <div className="w-16 h-16 bg-[#F25F19] rounded-sm flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold tracking-tight mb-4 group-hover:text-white transition-colors">
                  {value.title}
                </h3>
                <p className="text-[#71717A] group-hover:text-white/70 transition-colors">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Histoire du Logement Social Section */}
      <section data-testid="social-housing-section" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#F25F19] font-bold uppercase tracking-widest mb-4">Le logement social</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0A0A0A] tracking-tighter">
              Un pilier de l'habitat en France
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              <div className="bg-[#F4F4F5] p-8 rounded-sm">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#F25F19] rounded-sm flex items-center justify-center">
                    <History className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0A0A0A]">Les origines</h3>
                </div>
                <p className="text-[#71717A]">
                  Le logement social en France trouve ses racines au XIXe siècle avec les premières 
                  initiatives philanthropiques. La loi Siegfried de 1894 marque le début officiel 
                  des Habitations à Bon Marché (HBM), ancêtres des HLM actuels.
                </p>
              </div>

              <div className="bg-[#F4F4F5] p-8 rounded-sm">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#F25F19] rounded-sm flex items-center justify-center">
                    <Building className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0A0A0A]">L'essor de l'après-guerre</h3>
                </div>
                <p className="text-[#71717A]">
                  Après 1945, la France connaît une crise du logement majeure. Les grands ensembles 
                  émergent pour loger rapidement les populations. En 1950, les HLM (Habitations à 
                  Loyer Modéré) remplacent les HBM avec une mission sociale renforcée.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-[#F4F4F5] p-8 rounded-sm">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#F25F19] rounded-sm flex items-center justify-center">
                    <Home className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0A0A0A]">Aujourd'hui</h3>
                </div>
                <p className="text-[#71717A]">
                  Le parc social français compte plus de 5 millions de logements, hébergeant environ 
                  10 millions de personnes. Les bailleurs sociaux, comme nos partenaires (3F, Vivest, 
                  Maison & Cités), jouent un rôle crucial dans l'accès au logement pour tous.
                </p>
              </div>

              <div className="bg-[#0A0A0A] p-8 rounded-sm">
                <h3 className="text-xl font-bold text-white mb-4">Notre engagement</h3>
                <p className="text-white/70 mb-6">
                  PRO HOME Développement s'inscrit dans cette tradition en développant des programmes 
                  de qualité destinés aux bailleurs sociaux, contribuant ainsi à répondre aux besoins 
                  de logement des familles françaises.
                </p>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-[#F25F19] mono">250+</p>
                    <p className="text-white/60 text-sm">Logements sociaux</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-[#F25F19] mono">29</p>
                    <p className="text-white/60 text-sm">Programmes</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GFA Section */}
      <section data-testid="gfa-section" className="py-20 lg:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block bg-[#F25F19] text-white font-bold text-sm uppercase tracking-wider px-4 py-2 rounded-sm mb-6">
                Garantie
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tighter mb-6">
                GFA : Garantie Financière d'Achèvement
              </h2>
              <p className="text-white/70 text-lg mb-6">
                La Garantie Financière d'Achèvement (GFA) est un mécanisme essentiel 
                qui protège les acquéreurs contre les défaillances du promoteur et 
                assure l'achèvement du projet.
              </p>
              <p className="text-white/70 text-lg mb-8">
                Tous nos projets bénéficient de cette garantie, témoignant de notre 
                engagement envers la sécurité et la fiabilité de nos opérations.
              </p>
              <div className="flex items-center gap-4 p-4 bg-[#18181B] rounded-sm border-l-4 border-[#F25F19]">
                <Shield className="w-8 h-8 text-[#F25F19] flex-shrink-0" />
                <p className="text-white font-medium">
                  100% de nos projets sont couverts par la GFA
                </p>
              </div>
            </div>
            <div className="bg-[#18181B] p-8 lg:p-12 rounded-sm">
              <h3 className="text-2xl font-bold text-white mb-6">
                Protection garantie
              </h3>
              <ul className="space-y-4">
                {[
                  'Sécurisation du capital investi',
                  'Achèvement garanti du projet',
                  'Protection contre les défaillances',
                  'Conformité aux normes en vigueur',
                  'Transparence financière totale',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#F25F19] flex-shrink-0" />
                    <span className="text-white/80">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tighter mb-6">
            Construisons ensemble
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto mb-10">
            Vous avez un projet immobilier ? Notre équipe est à votre écoute pour 
            vous accompagner dans sa réalisation.
          </p>
          <Link to="/contact">
            <Button
              data-testid="cta-contact-btn"
              className="bg-[#F25F19] hover:bg-[#CC480B] text-white font-bold uppercase tracking-wide px-8 py-6 text-base rounded-sm"
            >
              Nous contacter
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
